import {observable} from "@nx-js/observer-util";
import {Services} from "../../Services";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {ICommunityFileModel} from "../../../models/community/files/ICommunityFileModel";
import {CommunityFileType} from "../../../models/community/files/CommunityFileType";
import {config} from "../../../config";
import {ICommunityFileSearchImagesModel} from "../../../models/community/files/ICommunityFileSearchImagesModel";
import {ICommunityFileDownloadImageResultModel} from "../../../models/community/files/ICommunityFileDownloadImageResultModel";
import {Resources} from "../../../resources/Resources";

export class CommunityFilesService {

    public static files: ICommunityFileModel[] = observable([]);

    public static dispose(): void {

    }

    // redimensionne + compresse une image trop lourde (ex: photo de téléphone) avant upload, pour éviter le 413 du proxy.
    // Ne touche pas : non-image, svg, gif (animé), image déjà légère (≤1 Mo), ou si la compression n'apporte aucun gain.
    private static async resizeImageForUpload(file: File): Promise<File> {
        const MAX_DIMENSION = 2400;
        const SAFE_BYTES = 1024 * 1024;

        if (!file.type.startsWith("image/") || file.type === "image/svg+xml" || file.type === "image/gif") {
            return file;
        }
        if (file.size <= SAFE_BYTES) {
            return file;
        }

        try {
            const bitmap = await createImageBitmap(file, {imageOrientation: "from-image"});
            const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
            const width = Math.max(1, Math.round(bitmap.width * scale));
            const height = Math.max(1, Math.round(bitmap.height * scale));

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext("2d");
            if (!context) {
                return file;
            }
            context.drawImage(bitmap, 0, 0, width, height);
            bitmap.close();

            const isPng = file.type === "image/png";
            const outputType = isPng ? "image/png" : "image/jpeg";
            const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(result => resolve(result), outputType, isPng ? undefined : 0.85));

            if (!blob || blob.size >= file.size) {
                return file;
            }

            const baseName = file.name.replace(/\.[^.]+$/, "");
            return new File([blob], `${baseName}.${isPng ? "png" : "jpg"}`, {type: outputType});
        } catch (exception) {
            return file;
        }
    }

    public static init(): void {
    }

    public static async create(communityId: string, fileType: CommunityFileType, file: File, progress?: (progress: number) => void, component?: INetworkComponent): Promise<ICommunityFileDownloadImageResultModel> {
        // photos volumineuses (ex: depuis un téléphone) → on redimensionne/compresse côté client pour éviter le 413 du proxy
        file = await this.resizeImageForUpload(file);

        let request = await Network.postFormData(ProductType.COMMUNITY, `/community/files`, {
            communityId: communityId,
            file: file,
            fileType: fileType
        }, component, {
            onUploadProgress: (progressEvent) => {
                if (progress) {
                    progress(progressEvent.loaded * 100 / progressEvent.total);
                }
            }
        });

        if (request.status == HttpStatus.OK) {
            const result = request.data as ICommunityFileDownloadImageResultModel;
            result.file = this.store(result.file);
            return result;
        }

        return undefined;
    }

    public static async searchImages(communityId: string, query: string, page: number, mySite: boolean, component?: INetworkComponent): Promise<ICommunityFileSearchImagesModel> {
        let request = await Network.post(ProductType.COMMUNITY, "/community/files/search/images",
            {
                communityId: communityId,
                query: query,
                page: page,
                mySite: mySite
            }, component);

        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async downloadImage(communityId: string, imageId: string, mySite: boolean, component?: INetworkComponent): Promise<ICommunityFileDownloadImageResultModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: Resources.t("words.imageNoLongerAvailable")}
        ]);

        let request = await Network.post(ProductType.COMMUNITY, "/community/files/download/image",
            {
                communityId: communityId,
                imageId: imageId,
                mySite: mySite
            }, component);

        if (request.status == HttpStatus.OK) {
            const result = request.data as ICommunityFileDownloadImageResultModel;
            result.file = this.store(result.file);
            return result;
        }
    }

    public static async downloadImageFromUrl(communityId: string, url: string, component?: INetworkComponent): Promise<ICommunityFileDownloadImageResultModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: Resources.t("words.imageNoLongerAvailable")}
        ]);

        let request = await Network.post(ProductType.COMMUNITY, "/community/files/download/url",
            {
                communityId: communityId,
                url: url,
            }, component);

        if (request.status == HttpStatus.OK) {
            const result = request.data as ICommunityFileDownloadImageResultModel;
            result.file = this.store(result.file);
            return result;
        }
    }

    /**
     * link
     */

    public static link(file: ICommunityFileModel) {
        return (config.httpCommunityUrl ?? config.domain) + `/community/files/${file.id}.${file.extension}`;
    }

    /**
     * store
     */

    public static storeAll(files: ICommunityFileModel[]): ICommunityFileModel[] {
        for (let key in files) {
            files[key] = this.store(files[key]);
        }
        return Services.storeAll(files);
    }

    public static store(file: ICommunityFileModel): ICommunityFileModel {
        file.link = this.link(file);
        return Services.store("id", this.files, file);
    }
}